/**
 * Commercial name: SEO-Presta
 * Proprietary-license whose copyright belongs to the Licensor PRESTA MODULE, a company registered under French law
 * under the number 528 531 767, located 90 rue Stanislas Torrents 13006 Marseille
 * support@presta-module.com https://www.presta-module.com
 *
 * SEO-Presta’ source codes are protected and Licensees are not allowed to create any Derivated Work.
 * The distribution or resale of SEO-Presta is strictly forbidden.
 *
 * SEO-Presta shall be used for Licensees’ own use, which allows Customization.
 *
 * Licensees shall not distribute nor resale publicly any Customization of SEO-Presta. However, Licensees are allowed
 * to sale a customization of SEO-Presta as part of a service provided to a designated customer.
 *
 * Limitation of Liability: Under no circumstances and under no legal theory, whether in tort (including negligence),
 * contract, or otherwise, shall the Licensor be liable to anyone for any indirect, special, incidental, or
 * consequential damages of any character arising as a result of this License including, without limitation, damages
 * for loss of goodwill, work stoppage, computer failure or malfunction, or any and all other commercial damages
 * or losses. This limitation of liability shall not apply to the extent applicable law prohibits such limitation.
 *
 * Attribution notice: SEO-Presta is a Derivated Work from an Original Work created by PrestaShop SA
 * and its contributors which can be found at https://github.com/PrestaShop/PrestaShop
 */
import LocalizationException from '../exception/localization';
import NumberSymbol from '../number-symbol';

class NumberSpecification {
    /**
     * Number specification constructor.
     *
     * @param string positivePattern CLDR formatting pattern for positive amounts
     * @param string negativePattern CLDR formatting pattern for negative amounts
     * @param NumberSymbol symbol Number symbol
     * @param int maxFractionDigits Maximum number of digits after decimal separator
     * @param int minFractionDigits Minimum number of digits after decimal separator
     * @param bool groupingUsed Is digits grouping used ?
     * @param int primaryGroupSize Size of primary digits group in the number
     * @param int secondaryGroupSize Size of secondary digits group in the number
     *
     * @throws LocalizationException
     */
    constructor(
        positivePattern,
        negativePattern,
        symbol,
        maxFractionDigits,
        minFractionDigits,
        groupingUsed,
        primaryGroupSize,
        secondaryGroupSize,
    ) {
        this.positivePattern = positivePattern;
        this.negativePattern = negativePattern;
        this.symbol = symbol;

        this.maxFractionDigits = maxFractionDigits;
        // eslint-disable-next-line
        this.minFractionDigits = maxFractionDigits < minFractionDigits ? maxFractionDigits : minFractionDigits;

        this.groupingUsed = groupingUsed;
        this.primaryGroupSize = primaryGroupSize;
        this.secondaryGroupSize = secondaryGroupSize;

        if (!this.positivePattern || typeof this.positivePattern !== 'string') {
            throw new LocalizationException('Invalid positivePattern');
        }

        if (!this.negativePattern || typeof this.negativePattern !== 'string') {
            throw new LocalizationException('Invalid negativePattern');
        }

        if (!this.symbol || !(this.symbol instanceof NumberSymbol)) {
            throw new LocalizationException('Invalid symbol');
        }

        if (typeof this.maxFractionDigits !== 'number') {
            throw new LocalizationException('Invalid maxFractionDigits');
        }

        if (typeof this.minFractionDigits !== 'number') {
            throw new LocalizationException('Invalid minFractionDigits');
        }

        if (typeof this.groupingUsed !== 'boolean') {
            throw new LocalizationException('Invalid groupingUsed');
        }

        if (typeof this.primaryGroupSize !== 'number') {
            throw new LocalizationException('Invalid primaryGroupSize');
        }

        if (typeof this.secondaryGroupSize !== 'number') {
            throw new LocalizationException('Invalid secondaryGroupSize');
        }
    }

    /**
     * Get symbol.
     *
     * @return NumberSymbol
     */
    getSymbol() {
        return this.symbol;
    }

    /**
     * Get the formatting rules for this number (when positive).
     *
     * This pattern uses the Unicode CLDR number pattern syntax
     *
     * @return string
     */
    getPositivePattern() {
        return this.positivePattern;
    }

    /**
     * Get the formatting rules for this number (when negative).
     *
     * This pattern uses the Unicode CLDR number pattern syntax
     *
     * @return string
     */
    getNegativePattern() {
        return this.negativePattern;
    }

    /**
     * Get the maximum number of digits after decimal separator (rounding if needed).
     *
     * @return int
     */
    getMaxFractionDigits() {
        return this.maxFractionDigits;
    }

    /**
     * Get the minimum number of digits after decimal separator (fill with "0" if needed).
     *
     * @return int
     */
    getMinFractionDigits() {
        return this.minFractionDigits;
    }

    /**
     * Get the "grouping" flag. This flag defines if digits
     * grouping should be used when formatting this number.
     *
     * @return bool
     */
    isGroupingUsed() {
        return this.groupingUsed;
    }

    /**
     * Get the size of primary digits group in the number.
     *
     * @return int
     */
    getPrimaryGroupSize() {
        return this.primaryGroupSize;
    }

    /**
     * Get the size of secondary digits groups in the number.
     *
     * @return int
     */
    getSecondaryGroupSize() {
        return this.secondaryGroupSize;
    }
}

export default NumberSpecification;
